import { useState, useEffect } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageContext";

export const ContactForm = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
   // honeypot field
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", message: "", website: "", formTime: Date.now(), jsToken: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFormData(prev => ({ ...prev, jsToken: btoa(navigator.userAgent + Date.now()) }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const endpoint = import.meta.env.VITE_CONTACT_FORM_ENDPOINT;

    if (!endpoint) {
      console.error("Contact form endpoint is not configured");
      toast({ title: t("contact.form.errorTitle"), description: t("contact.form.errorConfig"), variant: "destructive" });
      setIsSubmitting(false);
      return;
    }

    // Abort controller for timeout
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);  // 10 seconds timeout

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(formData),  // send honeypot automatically
        signal: controller.signal,
      });

      clearTimeout(timeout);

      let result;
      try {
        result = await response.json();
      } catch (e) {
        result = null;
      }

      if (!response.ok) {
        throw new Error(result?.error || t("contact.form.errorGeneric"));
      }

      toast({ title: t("contact.form.success"), description: t("contact.form.successDesc") });
      // reset including honeypot
      setFormData({ name: "", email: "", phone: "", subject: "", message: "", website: "", formTime: Date.now(), jsToken: btoa(navigator.userAgent + Date.now()) });
    } catch (error: any) {
      clearTimeout(timeout);
      if (error.name === "AbortError") {
        toast({ title: t("contact.form.errorTitle"), description: t("contact.form.errorTimeout"), variant: "destructive" });
      } else {
        toast({ title: t("contact.form.errorTitle"), description: error?.message || t("contact.form.errorGeneric"), variant: "destructive" });
      }
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="bg-secondary rounded-2xl p-8 shadow-xl">
      <h3 className="text-2xl font-bold text-foreground mb-6">{t("contact.form.title")}</h3>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input name="name" placeholder={t("contact.form.name")} value={formData.name} onChange={handleChange} required className="h-12" />
          <Input name="email" type="email" placeholder={t("contact.form.email")} value={formData.email} onChange={handleChange} required className="h-12" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input name="phone" type="tel" placeholder={t("contact.form.phone")} value={formData.phone} onChange={handleChange} className="h-12" />
          <Input name="subject" placeholder={t("contact.form.subject")} value={formData.subject} onChange={handleChange} className="h-12" />
        </div>
        <Textarea name="message" placeholder={t("contact.form.message")} value={formData.message} onChange={handleChange} required rows={5} className="resize-none" />

        {/* Honeypot field — invisible to humans, visible to bots */}
        {/* Fixed for accessibility: Added aria-label and aria-hidden */}
        <input
          type="text"
          name="website"
          value={formData.website}
          onChange={handleChange}
          autoComplete="off"
          tabIndex={-1}
          aria-hidden="true"
          aria-label="Do not fill this field if you are human"
          style={{ position: "absolute", left: "-9999px", top: "0", width: "1px", height: "1px", overflow: "hidden", display: "none" }}
        />

        {/* Anti-bot fields */}
        <input type="hidden" name="formTime" value={formData.formTime} />
        <input type="hidden" name="jsToken" value={formData.jsToken} />
        <Button type="submit" size="lg" className="w-full bg-accent hover:bg-orange-hover text-accent-foreground font-semibold" disabled={isSubmitting}>
          {isSubmitting ? t("contact.form.sending") : (<>{t("contact.form.submit")}<Send className="ml-2 w-5 h-5" /></>)}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;
